import React from "react";
import {
  BarChart3, Users, Heart, Building2, TrendingUp,
  Clock, ArrowUpRight, ArrowDownRight, Percent, Target,
  Zap, MapPin, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useExhibitionAnalytics } from "@/hooks/useExhibitionAnalytics";
import { cn } from "@/lib/utils";

const ExhibitionAnalyticsDashboard: React.FC = () => {
  const { analytics, loading, refetch } = useExhibitionAnalytics();

  if (loading) {
    return (
      <div className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-6">
        <p className="text-center text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  const { engagement, peakHours, conversions, topPerformingCities } = analytics;

  // Find peak hour
  const peakHour = peakHours.reduce((max, h) => 
    h.participants > max.participants ? h : max, peakHours[0]
  );

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}${period}`;
  };

  return (
    <div className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/40 dark:border-gray-700/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-foreground">Exhibition Analytics</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refetch}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Engagement Metrics */}
        <section>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Engagement Metrics
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard
              icon={Building2}
              label="Total Booths"
              value={engagement.totalBooths}
              subValue={`${engagement.activeBooths} active`}
              color="primary"
            />
            <MetricCard
              icon={Users}
              label="Participants"
              value={engagement.totalParticipants}
              color="green"
            />
            <MetricCard
              icon={Heart}
              label="Total Pops"
              value={engagement.totalPops}
              color="pop"
            />
            <MetricCard
              icon={Heart}
              label="Matches Made"
              value={engagement.totalMatches}
              subValue="from exhibition"
              color="pink"
            />
          </div>

          {/* Join Requests Stats */}
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="p-3 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-foreground">{engagement.totalJoinRequests}</p>
              <p className="text-xs text-muted-foreground">Total Requests</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-center">
              <p className="text-2xl font-bold text-green-600">{engagement.acceptedRequests}</p>
              <p className="text-xs text-green-700 dark:text-green-400">Accepted</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-center">
              <p className="text-2xl font-bold text-red-600">{engagement.rejectedRequests}</p>
              <p className="text-xs text-red-700 dark:text-red-400">Rejected</p>
            </div>
          </div>
        </section>

        {/* Conversion Rates */}
        <section>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Conversion Rates
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ConversionCard
              label="Pop → Match"
              rate={conversions.popToMatchRate}
              description="Pops that became matches"
            />
            <ConversionCard
              label="Request Accept"
              rate={conversions.requestAcceptRate}
              description="Join requests accepted"
            />
            <ConversionCard
              label="Engagement"
              rate={conversions.participantEngagementRate}
              description="Participants who popped"
            />
          </div>
        </section>

        {/* Peak Hours */}
        <section>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Peak Hours (Last 24h)
          </h4>
          
          {/* Peak hour highlight */}
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-800 dark:text-amber-200">
                  Peak Activity
                </span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-amber-900 dark:text-amber-100">
                  {formatHour(peakHour.hour)}
                </span>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {peakHour.participants} participants
                </p>
              </div>
            </div>
          </div>

          {/* Hourly bar chart */}
          <div className="flex items-end gap-0.5 h-20 bg-muted/20 rounded-lg p-2">
            {peakHours.map((hour, index) => {
              const maxParticipants = Math.max(...peakHours.map(h => h.participants), 1);
              const heightPercent = (hour.participants / maxParticipants) * 100;
              const isPeak = hour.hour === peakHour.hour;
              
              return (
                <div
                  key={index}
                  className="flex-1 flex flex-col items-center justify-end"
                  title={`${formatHour(hour.hour)}: ${hour.participants} participants`}
                >
                  <div
                    className={cn(
                      "w-full rounded-t transition-all",
                      isPeak ? "bg-amber-500" : "bg-primary/60"
                    )}
                    style={{ height: `${Math.max(heightPercent, 5)}%` }}
                  />
                  {index % 6 === 0 && (
                    <span className="text-[8px] text-muted-foreground mt-1">
                      {formatHour(hour.hour)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Top Cities */}
        <section>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Top Performing Cities
          </h4>
          <div className="space-y-2">
            {topPerformingCities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No city data yet
              </p>
            ) : (
              topPerformingCities.map((city, index) => (
                <div
                  key={city.cityName}
                  className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                >
                  <span className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                    index === 0 && "bg-amber-400 text-amber-900",
                    index === 1 && "bg-gray-300 text-gray-700",
                    index === 2 && "bg-amber-600 text-amber-100",
                    index > 2 && "bg-muted text-muted-foreground"
                  )}>
                    {index + 1}
                  </span>
                  <span className="text-lg">{city.flag}</span>
                  <span className="flex-1 font-medium text-foreground">{city.cityName}</span>
                  <span className="text-sm font-bold text-primary">{city.score} booths</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

interface MetricCardProps {
  icon: React.ElementType;
  label: string;
  value: number;
  subValue?: string;
  color: "primary" | "green" | "pop" | "pink";
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon: Icon,
  label,
  value,
  subValue,
  color,
}) => {
  const colorClasses = {
    primary: "text-primary",
    green: "text-green-600",
    pop: "text-pop",
    pink: "text-pink-500",
  };

  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-muted/50">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn("w-4 h-4", colorClasses[color])} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className={cn("text-2xl font-bold", colorClasses[color])}>{value}</p>
      {subValue && (
        <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
      )}
    </div>
  );
};

interface ConversionCardProps {
  label: string;
  rate: number;
  description: string;
}

const ConversionCard: React.FC<ConversionCardProps> = ({
  label,
  rate,
  description,
}) => {
  const isGood = rate >= 20;
  const isMedium = rate >= 10 && rate < 20;

  return (
    <div className={cn(
      "p-3 rounded-lg border",
      isGood && "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
      isMedium && "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      !isGood && !isMedium && "bg-muted/30 border-muted/50"
    )}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {isGood ? (
          <ArrowUpRight className="w-4 h-4 text-green-600" />
        ) : (
          <ArrowDownRight className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
      <p className={cn(
        "text-2xl font-bold",
        isGood && "text-green-600",
        isMedium && "text-amber-600",
        !isGood && !isMedium && "text-muted-foreground"
      )}>
        {rate.toFixed(1)}%
      </p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
};

export default ExhibitionAnalyticsDashboard;
